/**
 * Date formatting utilities
 * Centralized date formatting for consistent display across the app
 */

import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';

/**
 * Supported date format types
 */
export type DateFormatType =
  | 'dd/mm/yyyy'      // 25/01/2025
  | 'dd-mm-yyyy'      // 25-01-2025
  | 'yyyy-mm-dd'      // 2025-01-25 (ISO format)
  | 'dd MMM yyyy'     // 25 Jan 2025
  | 'dd MMMM yyyy'    // 25 January 2025
  | 'MMM dd, yyyy'    // Jan 25, 2025
  | 'MMMM dd, yyyy'   // January 25, 2025
  | 'relative';       // 2 hours ago, 3 days ago

/**
 * Format patterns for date-fns
 */
const FORMAT_PATTERNS: Record<Exclude<DateFormatType, 'relative'>, string> = {
  'dd/mm/yyyy': 'dd/MM/yyyy',
  'dd-mm-yyyy': 'dd-MM-yyyy',
  'yyyy-mm-dd': 'yyyy-MM-dd',
  'dd MMM yyyy': 'dd MMM yyyy',
  'dd MMMM yyyy': 'dd MMMM yyyy',
  'MMM dd, yyyy': 'MMM dd, yyyy',
  'MMMM dd, yyyy': 'MMMM dd, yyyy',
};

/**
 * Parse date input to Date object
 */
const parseDate = (date: string | Date | number): Date | null => {
  if (!date) return null;

  if (date instanceof Date) {
    return isValid(date) ? date : null;
  }

  if (typeof date === 'number') {
    const parsed = new Date(date);
    return isValid(parsed) ? parsed : null;
  }

  // Try parsing as ISO string
  const parsed = parseISO(date);
  if (isValid(parsed)) return parsed;

  // Try parsing as regular date string
  const fallback = new Date(date);
  return isValid(fallback) ? fallback : null;
};

/**
 * Format a date to the specified format
 *
 * @param date - Date to format (string, Date object, or timestamp)
 * @param formatType - Format type (default: 'dd/mm/yyyy')
 * @param fallback - Fallback string if date is invalid (default: '-')
 * @returns Formatted date string
 *
 * @example
 * formatDate('2025-01-25') // '25/01/2025'
 * formatDate('2025-01-25', 'dd MMM yyyy') // '25 Jan 2025'
 * formatDate('2025-01-25', 'relative') // '2 days ago'
 * formatDate(new Date(), 'MMM dd, yyyy') // 'Jan 25, 2025'
 */
export const formatDate = (
  date: string | Date | number | null | undefined,
  formatType: DateFormatType = 'dd/mm/yyyy',
  fallback: string = '-'
): string => {
  if (!date) return fallback;

  const parsedDate = parseDate(date);
  if (!parsedDate) return fallback;

  if (formatType === 'relative') {
    return formatDistanceToNow(parsedDate, { addSuffix: true });
  }

  return format(parsedDate, FORMAT_PATTERNS[formatType]);
};

/**
 * Format date with time
 *
 * @param date - Date to format
 * @param includeSeconds - Include seconds in output (default: false)
 * @param fallback - Fallback string if date is invalid
 * @returns Formatted date and time string (e.g., '25/01/2025 14:30')
 *
 * @example
 * formatDateTime('2025-01-25T14:30:00') // '25/01/2025 14:30'
 * formatDateTime('2025-01-25T14:30:45', true) // '25/01/2025 14:30:45'
 */
export const formatDateTime = (
  date: string | Date | number | null | undefined,
  includeSeconds: boolean = false,
  fallback: string = '-'
): string => {
  if (!date) return fallback;

  const parsedDate = parseDate(date);
  if (!parsedDate) return fallback;

  const pattern = includeSeconds ? 'dd/MM/yyyy HH:mm:ss' : 'dd/MM/yyyy HH:mm';
  return format(parsedDate, pattern);
};

/**
 * Format time only
 *
 * @param date - Date to format
 * @param includeSeconds - Include seconds in output (default: false)
 * @param fallback - Fallback string if date is invalid
 * @returns Formatted time string (e.g., '14:30')
 *
 * @example
 * formatTime('2025-01-25T14:30:00') // '14:30'
 * formatTime('2025-01-25T14:30:45', true) // '14:30:45'
 */
export const formatTime = (
  date: string | Date | number | null | undefined,
  includeSeconds: boolean = false,
  fallback: string = '-'
): string => {
  if (!date) return fallback;

  const parsedDate = parseDate(date);
  if (!parsedDate) return fallback;

  const pattern = includeSeconds ? 'HH:mm:ss' : 'HH:mm';
  return format(parsedDate, pattern);
};

/**
 * Format relative time (e.g., '2 hours ago', 'in 3 days')
 *
 * @param date - Date to format
 * @param fallback - Fallback string if date is invalid
 * @returns Relative time string
 *
 * @example
 * formatRelativeTime('2025-01-23T10:00:00') // '2 days ago'
 */
export const formatRelativeTime = (
  date: string | Date | number | null | undefined,
  fallback: string = '-'
): string => {
  return formatDate(date, 'relative', fallback);
};

/**
 * Check if a date is valid
 *
 * @param date - Date to check
 * @returns Boolean indicating if date is valid
 */
export const isValidDate = (date: string | Date | number | null | undefined): boolean => {
  if (!date) return false;
  return parseDate(date) !== null;
};
